import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, ArrowSquareOut } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  LICENSE: { en: 'License', it: 'Licenza' },
  TERMS: { en: 'Terms & Conditions', it: 'Termini e Condizioni' },
  PRIVACY: { en: 'Privacy Policy', it: 'Privacy Policy' },
  RISK: { en: 'Risk Disclosure', it: 'Informativa sul Rischio' },
  AGREEMENT: { en: 'Client Agreement', it: 'Contratto Cliente' },
  REGULATORY: { en: 'Regulatory Document', it: 'Documento Regolamentare' },
  OTHER: { en: 'Other Document', it: 'Altro Documento' },
};

const LegalDocuments = () => {
  const { language, t } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/legal-documents`)
      .then(res => setDocs(res.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const grouped = docs.reduce((acc, d) => {
    const key = d.doc_type || 'OTHER';
    (acc[key] = acc[key] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="p-8" data-testid="legal-documents-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">{language === 'it' ? 'Documenti Legali' : 'Legal Documents'}</h1>
        <p className="text-slate-600 mt-1">{language === 'it' ? 'Documenti ufficiali pubblicati da WellingtonCDFS.' : 'Official documents published by WellingtonCDFS.'}</p>
      </div>

      {loading ? <div className="loader" /> : docs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <FileText size={40} className="mx-auto text-slate-400 mb-3" />
          <p className="text-slate-600">{language === 'it' ? 'Nessun documento pubblicato al momento.' : 'No documents published yet.'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([type, list]) => (
            <div key={type} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`legal-group-${type}`}>
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                <h2 className="font-bold text-slate-800">{TYPE_LABEL[type]?.[language] || type}</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {list.map(d => (
                  <div key={d.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50" data-testid={`legal-doc-${d.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={22} weight="duotone" className="text-brand-accent shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{d.title}</div>
                        {d.description && <div className="text-sm text-slate-500 truncate">{d.description}</div>}
                        <div className="text-xs text-slate-400 mt-0.5">
                          {(d.language || 'EN').toUpperCase()} · v{d.version || '1.0'} · {new Date(d.published_at || d.updated_at || d.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={d.file_url} target="_blank" rel="noreferrer" data-testid={`legal-open-${d.id}`} className="p-2 text-slate-600 hover:text-brand-accent" title={language === 'it' ? 'Apri' : 'Open'}>
                        <ArrowSquareOut size={18} />
                      </a>
                      <a href={d.file_url} download data-testid={`legal-download-${d.id}`} className="p-2 text-slate-600 hover:text-brand-accent" title={language === 'it' ? 'Scarica' : 'Download'}>
                        <Download size={18} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LegalDocuments;
